import React from 'react';
import { Avatar, Icon, MOCK, StatusChip } from './sp-primitives';
import { useApp } from './sp-shell';
import { ROUTES } from '../routes';

function ToastStack() {
  const app = useApp();
  const ICONS = { success: 'check', error: 'alert', info: 'bell' };
  return (
    <div className="sp-toast-stack">
      {app.toasts.map((t) => {
        const kind = t.kind === 'success' || t.kind === 'error' ? t.kind : 'info';
        return (
          <div key={t.id} className={`sp-toast sp-toast--${kind}`}>
            <div className={`sp-toast__icon sp-toast__icon--${kind}`}>
              <Icon name={ICONS[kind]} size={15} stroke={2.2} />
            </div>
            <div className="sp-toast__body">
              <div className="sp-toast__title">{t.title}</div>
              {t.desc && <div className="sp-toast__desc">{t.desc}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PersistentErrorStack() {
  const app = useApp();
  const items = Array.isArray(app.persistentErrors) ? app.persistentErrors : [];
  if (!items.length) return null;
  return (
    <div className="sp-persistent-errors" role="alert" aria-live="assertive">
      {items.map((e) => (
        <div key={e.id} className="sp-creditors-banner sp-creditors-banner--err sp-persistent-errors__item">
          <div className="sp-persistent-errors__main">
            <strong>{e.title || 'Erro crítico no backend'}</strong>
            <div>{e.message || 'Falha inesperada.'}</div>
            {e.path ? <div className="sp-mono sp-persistent-errors__meta">Rota: {e.path}</div> : null}
            {e.detail ? <div className="sp-mono sp-persistent-errors__meta">{e.detail}</div> : null}
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-btn-compact" onClick={() => app.dismissPersistentError?.(e.id)}>
            <Icon name="x" size={12} />
          </button>
        </div>
      ))}
      {items.length > 1 ? (
        <div className="sp-persistent-errors__clear">
          <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => app.clearPersistentErrors?.()}>
            Limpar todos
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Modal({ open, onClose, children, width = 720 }) {
  if (!open) return null;
  return (
    <div className="sp-modal-backdrop" onClick={onClose} role="presentation">
      <div className="sp-modal-panel" style={{ width }} onClick={(e) => e.stopPropagation()} role="dialog">
        {children}
      </div>
    </div>
  );
}

function CompanyPicker() {
  const app = useApp();
  const activeCompanies = app.companies.filter((c) => c.status === 'active');
  return (
    <Modal open={app.companyPickerOpen} onClose={() => app.setCompanyPickerOpen(false)} width={460}>
      <div className="sp-modal__head">
        <div>
          <div className="sp-modal__head-title">Trocar empresa ativa</div>
          <div className="sp-modal__head-desc">Os dados serão filtrados pela empresa escolhida.</div>
        </div>
        <button type="button" onClick={() => app.setCompanyPickerOpen(false)} className="sp-btn sp-btn-ghost sp-modal__close"><Icon name="x" size={14} /></button>
      </div>
      <div className="sp-modal__body">
        {activeCompanies.map((c) => {
          const active = c.id === app.activeCompanyId;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                app.setActiveCompanyId(c.id);
                app.setCompanyPickerOpen(false);
                app.toast('info', 'Empresa trocada', `Agora você está em ${c.name}.`);
              }}
              className={`sp-modal-picker__btn${active ? ' is-active' : ''}`}
            >
              <div className="sp-modal-picker__avatar">{c.name[0]}</div>
              <div className="sp-modal-picker__main">
                <div className="sp-modal-picker__name">{c.name}</div>
                <div className="sp-mono sp-modal-picker__meta">company_id:{c.id}</div>
              </div>
              <StatusChip kind={c.status === 'active' ? 'green' : 'amber'}>{c.status || 'status'}</StatusChip>
              {active && <div className="sp-modal-picker__check"><Icon name="check" size={16} stroke={2.5} /></div>}
            </button>
          );
        })}
        {activeCompanies.length === 0 && (
          <div className="sp-modal__empty">
            Nenhuma empresa ativa disponível para seleção.
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="sp-btn sp-btn-accent"
                onClick={() => {
                  app.setCompanyPickerOpen(false);
                  app.navigate(ROUTES.COMPANY_NEW);
                }}
              >
                Criar Empresa
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function DeletePaymentConfirmDialog({ open, batchId, onCancel, onConfirm, busy }) {
  const handleBackdropClose = () => {
    if (!busy) onCancel();
  };
  return (
    <Modal open={open} onClose={handleBackdropClose} width={440}>
      <div className="sp-modal__head sp-modal__head--start">
        <div>
          <div className="sp-modal__head-title">Excluir pagamento</div>
          <div className="sp-modal__head-desc">Esta ação remove o lote do Sienge Pay e não pode ser desfeita.</div>
        </div>
        <button type="button" onClick={onCancel} className="sp-btn sp-btn-ghost sp-modal__close" disabled={busy} aria-label="Fechar"><Icon name="x" size={14} /></button>
      </div>
      <div className="sp-modal__body">
        <p className="sp-delete-confirm-msg">
          Excluir o lote <span className="sp-mono sp-fw-700">{batchId || '—'}</span>? Os dados deste pagamento serão removidos do Sienge Pay.
        </p>
      </div>
      <div className="sp-modal__footer">
        <button type="button" className="sp-btn sp-btn-ghost" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button type="button" className="sp-btn sp-btn-outline sp-btn-delete-confirm" onClick={onConfirm} disabled={busy}>
          {busy ? 'A excluir…' : 'Excluir'}
        </button>
      </div>
    </Modal>
  );
}

function DiffModal() {
  const app = useApp();
  const batch = app.diffModalBatch;
  if (!batch) return null;
  const divs = MOCK.divergences;
  return (
    <Modal open={!!batch} onClose={() => app.setDiffModalBatch(null)} width={820}>
      <div className="sp-modal__head sp-modal__head--start">
        <div>
          <div className="sp-caps sp-modal__caps">Auditoria · {batch.id}</div>
          <div className="sp-modal__head-title-lg">Divergências financeiras</div>
          <div className="sp-modal__head-desc">
            {batch.template} · {batch.file} · {batch.colab} colaboradores · {divs.length} divergências
          </div>
        </div>
        <button type="button" onClick={() => app.setDiffModalBatch(null)} className="sp-btn sp-btn-ghost sp-modal__close"><Icon name="x" size={14} /></button>
      </div>
      <div className="sp-modal__body--muted">
        {divs.map((d, i) => {
          const isNew = d.diff === 'novo';
          const pct = isNew ? 0 : parseFloat(d.diff);
          const kind = isNew ? 'amber' : pct > 5 ? 'amber' : pct < -5 ? 'red' : 'green';
          return (
            <div key={i} className="sp-card sp-diff-card">
              <div className="sp-diff-card__row">
                <Avatar name={d.name} size={36} />
                <div className="sp-diff-card__info">
                  <div className="sp-diff-card__name">{d.name}</div>
                  <div className="sp-mono sp-diff-card__meta">{d.cpf} · {d.cc}</div>
                </div>
                <StatusChip kind={kind}>{isNew ? 'NOVO CADASTRO' : `Δ ${d.diff}`}</StatusChip>
              </div>
              <div className="sp-diff-card__grid">
                <div className="sp-diff-card__box sp-diff-card__box--neutral">
                  <div className="sp-caps sp-diff-card__box-cap">Último pagamento</div>
                  <div className={`sp-num sp-diff-card__value${d.last ? '' : ' sp-diff-card__value--muted'}`}>
                    {d.last ? `R$ ${d.last.toLocaleString('pt-BR')},00` : 'Não existe'}
                  </div>
                  <div className="sp-diff-card__hint">
                    {d.last ? 'mar/2026 · lote L-454' : 'Credenciado será criado'}
                  </div>
                </div>
                <div className="sp-diff-card__arrow"><Icon name="arrow" size={18} /></div>
                <div className="sp-diff-card__box sp-diff-card__box--highlight">
                  <div className="sp-caps sp-diff-card__box-cap sp-diff-card__box-cap--green">Valor atual</div>
                  <div className="sp-num sp-diff-card__value">R$ {d.now.toLocaleString('pt-BR')},00</div>
                  <div className="sp-diff-card__hint">abr/2026 · lote {batch.id}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="sp-modal__footer">
        <button type="button" className="sp-btn sp-btn-ghost" onClick={() => app.setDiffModalBatch(null)}>Fechar</button>
        <div className="sp-modal__footer-actions">
          <button type="button" className="sp-btn sp-btn-outline" onClick={() => {
            app.rejectBatch(batch.id); app.setDiffModalBatch(null);
          }}>Rejeitar</button>
          <button type="button" className="sp-btn sp-btn-accent" onClick={() => {
            app.approveBatch(batch.id); app.setDiffModalBatch(null);
          }}><Icon name="check" size={13} stroke={2.4} /> Aprovar com divergências</button>
        </div>
      </div>
    </Modal>
  );
}

function ConfirmModal({ open, title, message, confirmLabel = 'Confirmar', onConfirm, onCancel, busy }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={busy ? null : onCancel} width={440}>
      <div className="sp-modal__head sp-modal__head--start">
        <div>
          <div className="sp-modal__head-title">{title || 'Confirmar'}</div>
          {message && <div className="sp-modal__head-desc">{message}</div>}
        </div>
        {!busy && <button type="button" onClick={onCancel} className="sp-btn sp-btn-ghost sp-modal__close" aria-label="Fechar"><Icon name="x" size={14} /></button>}
      </div>
      <div className="sp-modal__footer">
        <button type="button" className="sp-btn sp-btn-ghost" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button type="button" className="sp-btn sp-btn-accent" onClick={onConfirm} disabled={busy}>
          {busy ? 'Aguarde…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

export { ToastStack, PersistentErrorStack, Modal, CompanyPicker, DeletePaymentConfirmDialog, DiffModal, ConfirmModal };
